import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { BrandLogo } from '../components/BrandLogo';
import { track } from '../lib/analytics';

export default function Login() {
  const { t, lang } = useLanguage();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get('mode') === 'signup' ? 'signup' : 'login');
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const finish = (data) => {
    setSession(data.token, data.user);
    if (data.user?.preferred_language) localStorage.setItem('app_language', data.user.preferred_language);
    track(mode === 'signup' ? 'signup' : 'login', { method: 'email' });
    const next = params.get('next');
    navigate(next && next.startsWith('/') ? next : '/dashboard');
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'signup') {
        const { data } = await api.post('/auth/register', { name, email, password, preferred_language: lang });
        finish(data);
      } else {
        const { data } = await api.post('/auth/login', { email, password });
        finish(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    track('login', { method: 'google' });
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = 'min-h-[48px] w-full rounded-md border border-input bg-white px-3.5 py-2.5 text-base sm:text-sm text-primary outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20';

  return (
    <div className="min-h-screen bg-surface/40">
      <div className="safe-bottom mx-auto flex max-w-md flex-col px-4 py-5 sm:py-8">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="auth-brand" className="flex items-center gap-2">
            <span className="logo-mark flex h-10 w-10 items-center justify-center rounded-lg"><BrandLogo className="h-9 w-9 object-contain" /></span>
            <span className="logo-text font-heading text-lg font-bold">{t('brand.name')}</span>
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="mt-6 rounded-xl border border-border bg-white p-5 shadow-sm sm:mt-10 sm:p-7">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-primary">{mode === 'signup' ? t('auth.create') : t('auth.welcome')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{mode === 'signup' ? t('auth.signupSubtitle') : t('auth.loginSubtitle')}</p>

          <button data-testid="google-signin-button" onClick={handleGoogle} className="mt-6 flex w-full items-center justify-center gap-2 rounded-md border border-border bg-white px-4 py-2.5 text-sm font-semibold text-primary transition-all hover:border-primary/40 hover:-translate-y-0.5">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
            {t('auth.continueGoogle')}
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" /><span className="text-xs uppercase tracking-wide text-muted-foreground">{t('auth.or')}</span><div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleEmail} className="space-y-3">
            {mode === 'signup' && (
              <div>
                <label htmlFor="signup-name" className="mb-1 block text-xs font-medium text-muted-foreground">{t('auth.name')}</label>
                <input id="signup-name" data-testid="signup-name-input" name="name" autoComplete="name" enterKeyHint="next" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}
            <div>
              <label htmlFor="auth-email" className="mb-1 block text-xs font-medium text-muted-foreground">{t('auth.email')}</label>
              <input id="auth-email" data-testid="email-input" name="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} enterKeyHint="next" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label htmlFor="auth-password" className="mb-1 block text-xs font-medium text-muted-foreground">{t('auth.password')}</label>
              <input id="auth-password" data-testid="password-input" name="password" type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} enterKeyHint="go" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            </div>
            <button data-testid="email-submit-button" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'signup' ? t('auth.signupBtn') : t('auth.loginBtn')}
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            {mode === 'signup' ? t('auth.hasAccount') : t('auth.noAccount')}{' '}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')} className="font-semibold text-accent hover:underline">
              {mode === 'signup' ? t('auth.loginLink') : t('auth.signupLink')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
